// HUD Mood Context — the "it's alive" scene-reactivity layer (style guide §6).
//
// A SINGLE subscription to EVENTS.SCENE_MOOD (emitted by the atmosphere drivers,
// previously unconsumed by the UI). The provider holds the live intensity and
// exposes a pre-clamped panel tint token; components call useHudMood() and read
// the token — they never touch the bus, so mood costs one subscription total,
// not one per restyled surface.
//
// The tint is clamped to ~6% alpha and only ever tints the panel BASE — never
// text or bar colors (legibility never trades, §7). `?hudMood=0` disables it.
//
// Nothing consumes this yet; Lanes B/D read the token on the player frame /
// panels. Mounting the provider with no consumer is a visual no-op.

import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { MOOD, moodTintFor } from '../theme/colors';
import { bootSearchParams } from '../../../config/boot/bootUrl';

interface HudMoodValue {
  /** Pre-clamped rgba tint for the panel base; `MOOD.neutral` when disabled. */
  moodTint: string;
  /** Raw SCENE_MOOD intensity multiplier (1 = neutral baseline). */
  intensity: number;
  /** False when `?hudMood=0` — the tint is pinned neutral. */
  enabled: boolean;
}

const NEUTRAL: HudMoodValue = { moodTint: MOOD.neutral, intensity: 1, enabled: false };

const HudMoodContext = createContext<HudMoodValue>(NEUTRAL);

/**
 * Read the current HUD mood token. Safe outside a provider (returns the neutral
 * default) — mood is decorative, so a missing provider must never throw and
 * break the HUD.
 */
export const useHudMood = (): HudMoodValue => useContext(HudMoodContext);

/** `?hudMood=0` opts out of the reactive tint entirely. */
function moodEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return bootSearchParams().get('hudMood') !== '0';
  } catch {
    return true;
  }
}

export const HudMoodProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const enabled = useMemo(moodEnabled, []);
  const [intensity, setIntensity] = useState(1);

  useEffect(() => {
    if (!enabled) return;
    let bus: EventBus;
    try {
      bus = EventBus.getInstance();
    } catch {
      return; // no bus (some test harnesses) — stay neutral
    }
    const onMood = (d: { intensity?: number }) => {
      if (typeof d?.intensity === 'number' && Number.isFinite(d.intensity)) {
        setIntensity(d.intensity);
      }
    };
    return bus.on(EVENTS.SCENE_MOOD, onMood);
  }, [enabled]);

  const value = useMemo<HudMoodValue>(
    () => ({
      enabled,
      intensity: enabled ? intensity : 1,
      moodTint: enabled ? moodTintFor(intensity) : MOOD.neutral,
    }),
    [enabled, intensity],
  );

  return <HudMoodContext.Provider value={value}>{children}</HudMoodContext.Provider>;
};
